import { googleLogout } from '@react-oauth/google';
import { message } from 'antd';
import Cookies from 'js-cookie';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import baseConfig from '@/config';
import {
  cancelUser,
  checkLogin,
  emailLogin,
  getEmailCode,
  getPhoneCode,
  getUserInfo,
  type LoginOrInsert,
  loginOrInsert,
  updatePassword,
} from '@/services/login';
import {
  ciphertext,
  getCookie,
  isAllDigits,
  isValidEmail,
  removeCookie,
  setCookie,
} from '@/utils';
import OnlineTimeTracker from '@/utils/OnlineTimeTracker';
interface AuthLoginType {
  accessToken?: string;
  accountKey: string; // apple google
  authCode?: string;
  accountId: string; // apple google
}

// 1-输入邮箱和密码UI 2-只输入邮箱UI 3-输入手机验证码UI 4-输入密码UI 5-设置密码UI 6-输入邮箱验证码UI
export enum LoginUIState {
  INPUT_EMAIL_PASSWORD = 1,
  INPUT_EMAIL = 2,
  INPUT_PHONE_CODE = 3,
  INPUT_PASSWORD = 4,
  SET_PASSWORD = 5,
  INPUT_EMAIL_CODE = 6,
}

export type LoginStoreProps = {
  sendCodeState: boolean;
  isNeedSetupPassword: boolean;
  loginUIState: LoginUIState;
  countDown: number;
  salt?: string;
  startCountDown(accountId: string): void;
  getPhoneCode(accountId: string): Promise<any>;
  timer: NodeJS.Timeout | undefined;
  setUIState: (value: number) => void;
  checkLoginAccount(
    accountId: string,
    t: (id: string, values?: Record<string, any>) => any,
  ): void;
  loginWithCode(accountId: string, code: string): Promise<any>;
  loginWithPassword(accountId: string, password: string, oldSalt: string): Promise<any>;
  savePassword(accountId: string, password: string): void;
  setNeedSetupPassword: (value: boolean) => void;
  clearCoutDownState: () => void;
  loginLoading: boolean;
  getUserInfo(): Promise<any>;
  userInfo: Record<any, any>;
  authLogin(params: AuthLoginType): Promise<any>;
  logout(): void;
  agreeState: boolean;
  setAgreeState: (value: boolean) => void;
  resetLoginStore: any;
  justCancelUser: () => void;

  isNewUser?: boolean; // 是否新用户
  setIsNewUser?: (value: boolean) => void; // 设置是否新用户
};

// const useLoginStore = createWithEqualityFn<LoginStoreProps>()(
const useLoginStore = createWithEqualityFn<any>()(
  devtools(
    (set, get) => ({
      isNewUser: false, // 是否新用户
      setIsNewUser: (value: boolean) => {
        set({ isNewUser: value });
      },

      timer: undefined,
      sendCodeState: false,
      countDown: 60,
      isNeedSetupPassword: false,
      salt: undefined,
      // 点击发送验证码
      startCountDown: async (accountId: string) => {
        const { sendCodeState, getPhoneCode } = get();
        if (sendCodeState) return;
        set({ sendCodeState: true });
        try {
          const sendStatus = await getPhoneCode(accountId);
          if (sendStatus.code === 200) {
            // 立即更新一次倒计时
            set({ countDown: 59 }); // 从59开始，因为已经过去1秒

            const startTimer = () => {
              const { countDown } = get();
              if (countDown <= 1) {
                // 重置倒计时秒数
                set({ sendCodeState: false, countDown: 60 });
                return;
              }
              set({ countDown: countDown - 1 });
              // 设置下一次timer
              const timer = setTimeout(startTimer, 1000);
              set({ timer });
            };
            // 启动首次timer
            const timer = setTimeout(startTimer, 1000);
            set({ timer });
          } else {
            set({ sendCodeState: false });
          }
          return sendStatus;
        } catch (e) {
          set({ sendCodeState: false });
          message.error('网络错误');
        }
      },
      // 点击发送邮箱验证码
      startEmailCountDown: async (accountId: string) => {
        const { sendCodeState } = get();
        if (sendCodeState) return;
        try {
          const sendStatus = await getEmailCode(accountId);
          if (sendStatus.code === 200 || sendStatus.code === 10103011) {
            set({ sendCodeState: true });

            // 立即更新一次倒计时
            set({ countDown: 59 }); // 从59开始，因为已经过去1秒

            const startTimer = () => {
              const { countDown } = get();
              if (countDown <= 1) {
                // 重置倒计时秒数
                set({ sendCodeState: false, countDown: 60 });
                return;
              }
              set({ countDown: countDown - 1 });
              // 设置下一次timer
              const timer = setTimeout(startTimer, 1000);
              set({ timer });
            };
            // 启动首次timer
            const timer = setTimeout(startTimer, 1000);
            set({ timer });
          } else {
            set({ sendCodeState: false });
          }
          return sendStatus;
        } catch (e) {
          set({ sendCodeState: false });
          message.error('网络错误');
        }
      },
      // 获取验证码
      getPhoneCode: async (accountId: string) => {
        try {
          const res = await getPhoneCode({ phone: accountId });
          return res;
        } catch (error) {
          console.log(error);
          message.error('发送失败');
          set({ sendCodeState: false });
        }
      },
      loginLoading: false,
      loginUIState: 1,
      setUIState: (value: number) => {
        set({ loginUIState: value });
      },
      checkLoginAccount: async (
        accountId: string,
        t: (id: string, values?: Record<string, any>) => any,
      ) => {
        set({ loginLoading: true });
        /************密码登录***********/
        // 检查是否已注册
        // accountType 账号类型，手机验证码登录注册：48，密码登录：65
        // 默认检查类型为 65
        let accountType = 65;
        const isPhoneAccount = isAllDigits(accountId);
        // if (isPhoneAccount) {
        //   accountType = 48;
        // }
        let newLoginUIState = get().loginUIState;
        // 这里要同时检查 账号 密码登录和验证码登录的注册状态，因为如果有密码，只请求验证码登录的话salt不会返回，就不知道用户是否已经设置了密码
        try {
          // 临时 检查用户当前网络状态
          await checkLogin({
            accountType: 48,
            accountId: accountId,
          });
        } catch (error) {
          console.error('checkLogin error', error);
          if (navigator.onLine) {
            message.error(t('merror.0.5.wrong'));
          }
        }
        const checkRes = await checkLogin({
          accountType: 48,
          accountId: accountId,
        });

        const checkResPassword = await checkLogin({
          accountType: 65,
          accountId: accountId,
        });

        const checkResEmailCode = await checkLogin({
          accountType: 60,
          accountId: accountId,
        });

        if (checkResPassword.data) {
          if (checkResPassword.data.state === 3) {
            checkRes.data = checkResPassword.data;
          }
        }
        if (checkResEmailCode.data) {
          if (checkResEmailCode.data.state === 3) {
            checkRes.data.state = checkResEmailCode.data.state;
          }
        }

        // 检查注册状态
        if (checkRes.data.state === 3) {
          if (checkRes.data.salt) {
            // 已注册已设置密码
            newLoginUIState = 4;
            set({ salt: checkRes.data.salt });
          } else {
            // 已注册但未设置密码
            if (isPhoneAccount) {
              newLoginUIState = 3;
            } else {
              newLoginUIState = 6;
            }
          }
          set({ loginLoading: false });
        } else if (checkRes.data.state === 1) {
          // 未注册
          if (isPhoneAccount) {
            newLoginUIState = 3;
          } else {
            newLoginUIState = 6;
          }
          set({ loginLoading: false, isNeedSetupPassword: true, isNewUser: true });
        }

        if (String(checkRes.code) === '12301002') {
          set({ loginLoading: false });
          console.error('时间或者其他鉴权失败');
        }

        if (newLoginUIState !== get().loginUIState) {
          set({ loginUIState: newLoginUIState });
        }
        return newLoginUIState;
      },
      loginWithCode: async (accountId: string, code: string) => {
        set({ loginLoading: true });

        let loginRes;
        if (isValidEmail(accountId)) {
          loginRes = await emailLogin({
            accountId,
            code: code,
          });
        } else {
          // 手机验证码登录
          loginRes = await loginOrInsert({
            accountId: accountId,
            code: code,
            accountType: 48,
          });
        }

        console.log('loginRes', loginRes);
        if (loginRes?.code !== 200) {
          set({ loginLoading: false });
          loginRes.code = loginRes.data.code;
          return loginRes || {};
        }

        let accountType = 48;
        if (isValidEmail(accountId)) {
          accountType = 60;
        }
        // 登录成功 保存token信息
        setCookie('user', loginRes.data, 30);

        setCookie('accountType', accountType);
        setCookie('accountId', accountId);
        // 获取用户信息
        await get().getUserInfo(accountType);
        set({ loginLoading: false });

        return loginRes;
      },
      savePassword: async (accountId: string, password: string, oldSalt: '') => {
        set({ loginLoading: true });
        const { salt, newPassword } = await ciphertext(oldSalt, password);

        const updatePasswordRes = await updatePassword({
          accountId: accountId,
          salt,
          newPassword,
        });
        set({ loginLoading: false });
        return updatePasswordRes;
      },
      loginWithPassword: async (accountId: string, password: string, oldSalt: string) => {
        set({ loginLoading: true });
        // 生成密码密文和盐值
        const { salt, oldPassword, newPassword } = await ciphertext(oldSalt, password);
        // 密码登录
        const loginRes = await loginOrInsert({
          accountId: accountId,
          accountType: 65,
          zoneCode: '86',
          salt,
          newPassword, //  新密码密文，密码登录时必须
          oldPassword, //  旧密码密文，密码登录时必须
          isResult: false, //	非必须 是否需要返回用户信息
        });

        set({ loginLoading: false });

        if (loginRes.code !== 200) {
          // 因为服务器的失败之后的code在data里面，所以这里要兼容一下到外面判断
          loginRes.code = loginRes.data.code;
          return loginRes;
        }

        // 登录成功 保存token信息
        setCookie('user', loginRes.data, 30);

        setCookie('accountType', 65);
        setCookie('accountId', accountId);
        // 获取用户信息
        await get().getUserInfo(65);

        return loginRes;
      },
      setNeedSetupPassword: (value: boolean) => {
        set({ isNeedSetupPassword: value });
      },
      clearCoutDownState: () => {
        const { timer } = get();
        // 重置倒计时秒数
        set({ sendCodeState: false, countDown: 60 });
        // 清除定时器
        clearTimeout(timer);
      },
      userInfo: { avatarUrl: null, accountId: null, uid: null },
      // 获取用户信息
      getUserInfo: async (accountType: number) => {
        const userInfo = await getUserInfo(accountType);
        if (userInfo.code !== 200) return;
        set({ userInfo: userInfo.data });
        // 缓存账号，供下次登录预填写
        window.localStorage.setItem(
          'accountId',
          userInfo.data.accountId || userInfo.data.uid,
        );
        setCookie('userInfo', userInfo.data, 30);
      },
      // 第三方授权登录
      authLogin: async (params: AuthLoginType) => {
        const { getUserInfo } = get();
        const { accountKey, accessToken, authCode, accountId } = params;
        const authKeyAccountTypeDic: Record<string, number> = {
          apple: 53,
          google: 25,
        };
        let sendData: LoginOrInsert;
        if (accountKey === 'google') {
          sendData = {
            accountId,
            accountType: authKeyAccountTypeDic[accountKey],
            accessToken,
            byAccessToken: true,
          };
        } else {
          sendData = {
            accountId,
            accountType: authKeyAccountTypeDic[accountKey],
            authCode,
          };
        }

        const loginRes = await loginOrInsert(sendData);

        if (loginRes.code !== 200) {
          set({ loginLoading: false });
          return;
        }

        // 登录成功 保存token信息
        Cookies.set('user', JSON.stringify(loginRes.data));
        setCookie('accountType', authKeyAccountTypeDic[accountKey]);
        setCookie('accountId', accountId);
        // 获取用户信息
        await getUserInfo(authKeyAccountTypeDic[accountKey]);
        set({ loginLoading: false });

        return loginRes;
      },
      // 要保留的key
      ignoreKey: ['cookie_agree', 'taskCardShown'],
      // 退出登录
      logout: () => {
        const { timer } = get();

        removeCookie('user');
        removeCookie('userInfo');
        // localStorage.clear();
        // 获取 localStorage 中所有的 keys
        const keys = Object.keys(localStorage);

        // 遍历 keys 并删除除 "cookie_agree" 之外的其他数据
        keys.forEach((key) => {
          if (!get().ignoreKey.includes(key)) {
            localStorage.removeItem(key);
          }
        });
        googleLogout();
        // 重置状态
        set({ sendCodeState: false, countDown: 60 });
        // 清除定时器
        clearInterval(timer);
        const tracker = OnlineTimeTracker.getInstance();
        tracker.destroy();
        // 刷新当前页面而不是跳转到 home
        window.location.reload();
      },

      /**
       * 同意协议
       */
      agreeState: false,
      setAgreeState: (value: boolean) => {
        set({ agreeState: value });
      },
      resetLoginStore: () => {
        set({
          agreeState: false,
          timer: undefined,
          sendCodeState: false,
          countDown: 60,
          loginLoading: false,
          userInfo: { avatarUrl: null, accountId: null, uid: null },
        });
      },

      // 注销用户
      justCancelUser: async () => {
        let params = {
          accountType: getCookie('accountType'),
          accountId: getCookie('accountId'),
          deadline: 0,
        };
        let rsp = await cancelUser(params);
        if (rsp.success) {
          get().logout();
          // logout 函数内部已经会刷新页面，这里不需要再次跳转
        }
      },
    }),
    {
      name: 'login_store',
      enabled: baseConfig.debuggable,
    },
  ),
  shallow,
);

export default useLoginStore;
